<template>
  <v-container fluid class="pa-0">
    <!-- ===== FILA PRINCIPAL ===== -->
    <v-row>
      <!-- ============ PANEL IZQUIERDO: Formulario ============ -->
      <v-col cols="12" md="5">
        <v-card outlined class="rounded-lg pa-4">
          <div class="d-flex align-center mb-4">
            <v-icon color="primary" class="mr-2">mdi-cash-multiple</v-icon>
            <h3 class="text-h6 font-weight-bold mb-0">Apertura de Ciclo ({{ instancia }})</h3>
          </div>

          <!-- Selector de Ciclo ERP -->
          <v-row dense class="mb-0">
            <v-col cols="8">
              <v-autocomplete
                v-model="cicloErpSeleccionado"
                :items="ciclosErp"
                :item-text="c => c.ciclo"
                item-value="id_ciclo"
                label="Ciclo ERP"
                outlined dense
                prepend-inner-icon="mdi-school"
                :loading="loadingCiclos"
                :disabled="!puedeEditar"
                clearable
                return-object
                no-data-text="No hay ciclos disponibles"
              />
            </v-col>
            <v-col cols="4">
              <div class="d-flex flex-column align-center justify-center">
                <v-btn
                  color="secondary"
                  class="mb-2"
                  width="100%"
                  small
                  :loading="loadingPrecargar"
                  :disabled="!cicloErpSeleccionado || !puedeEditar"
                  @click="precargarDesdeCicloERP"
                >
                  <v-icon left small>mdi-lightning-bolt</v-icon>
                  Precargar
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="hasClonar" class="mb-2 mt-0">
            <v-col cols="12">
              <v-btn
                color="info darken-1"
                width="100%"
                small
                class="mt-0"
                :loading="loadingClonar"
                :disabled="!puedeEditar || !hasClonar"
                title="Copiar configuración del ciclo mas reciente"
                @click="clonarCicloAnterior"
              >
                <v-icon left small>mdi-content-copy</v-icon>
                Clonar Ciclo Anterior
              </v-btn>
            </v-col>
          </v-row>

          <v-row dense class="mb-2 mt-0">
            <v-col cols="6">
              <v-btn color="green darken-1" dark width="100%" small @click="descargarPlantilla">
                <v-icon left small>mdi-microsoft-excel</v-icon>
                Plantilla Excel
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="green darken-3"
                width="100%"
                small
                class="white--text"
                :loading="loadingImportar"
                :disabled="!puedeEditar"
                title="Llena el formulario con el Excel; después revisa y guarda"
                @click="$refs.archivoExcel.click()"
              >
                <v-icon left small>mdi-file-upload</v-icon>
                Importar Excel
              </v-btn>
              <input ref="archivoExcel" type="file" accept=".xlsx,.xls" style="display: none" @change="importarExcel">
            </v-col>
          </v-row>

          <v-alert v-if="importErrores.length" type="error" text dense dismissible class="mb-3" @input="importErrores = []">
            <div class="font-weight-bold mb-1">No se cargó el archivo:</div>
            <div v-for="(e, i) in importErrores" :key="'e' + i" class="caption">• {{ e }}</div>
          </v-alert>
          <v-alert v-if="importAvisos.length" type="warning" text dense dismissible class="mb-3" @input="importAvisos = []">
            <div class="font-weight-bold mb-1">Archivo cargado. Revisa antes de guardar:</div>
            <div v-for="(a, i) in importAvisos" :key="'a' + i" class="caption">• {{ a }}</div>
          </v-alert>

          <v-divider class="mb-3"/>

          <v-form ref="formConfig" v-model="valid" lazy-validation>
            <!-- Fechas -->
            <v-row dense>
              <v-col cols="6">
                <v-menu v-model="menuStart" :close-on-content-click="false" transition="scale-transition" min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="form.fecha_inicio" label="Fecha Inicio" prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" outlined dense :rules="[v => !!v || 'Requerido']" :disabled="!puedeEditar"/>
                  </template>
                  <v-date-picker v-model="form.fecha_inicio" @input="menuStart = false; calcularSemanas()" locale="es-mx" color="primary" :disabled="!puedeEditar"/>
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-menu v-model="menuEnd" :close-on-content-click="false" transition="scale-transition" min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="form.fecha_fin" label="Fecha Fin" prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" outlined dense :rules="[v => !!v || 'Requerido']" :disabled="!puedeEditar"/>
                  </template>
                  <v-date-picker v-model="form.fecha_fin" @input="menuEnd = false; calcularSemanas()" locale="es-mx" color="primary" :min="form.fecha_inicio" :disabled="!puedeEditar"/>
                </v-menu>
              </v-col>
            </v-row>

            <v-divider class="my-3"/>

            <v-row>
              <v-col cols="12">
                <span class="text-subtitle-2 font-weight-bold mb-2 grey--text text--darken-2">Venta total del ciclo anterior: {{ form.venta_total | currency }}</span>
              </v-col>
            </v-row>

            <v-divider class="my-3"/>

            <!-- Ingreso base y Estado -->
            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.ingreso_base"
                  label="Ingreso Base Proyectado ($)"
                  type="number" prefix="$" outlined dense
                  :disabled="!puedeEditar"
                  :rules="[v => !!v || 'Requerido', v => v > 0 || 'Debe ser mayor a cero']"
                  @input="calcularBolsa"
                  hint="= 10% de venta total del ciclo anterior"
                  persistent-hint
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="form.estado"
                  :items="estadosCiclo"
                  label="Estado del Ciclo"
                  outlined dense
                  :disabled="!puedeEditar"
                  prepend-inner-icon="mdi-list-status"
                />
              </v-col>
            </v-row>

            <!-- % Autorizado MKT -->
            <v-row dense align="center" class="mt-2">
              <v-col cols="8">
                <v-slider v-model="form.porcentaje_autorizado" label="% Autorizado de MKT" min="1" max="100" thumb-label="always" color="secondary" track-color="grey lighten-2" :disabled="!puedeEditar" @change="calcularBolsa"/>
              </v-col>
              <v-col cols="4">
                <v-text-field v-model.number="form.porcentaje_autorizado" type="number" suffix="%" outlined dense hide-details :disabled="!puedeEditar" @input="calcularBolsa"/>
              </v-col>
            </v-row>

            <v-alert type="info" text dense class="mt-2 text-subtitle-2 font-weight-bold" color="primary">
              Bolsa Total Calculada: {{ form.bolsa_total | currency }}
            </v-alert>

            <v-divider class="my-3"/>

            <!-- NI / RI -->
            <div class="text-subtitle-2 font-weight-bold mb-2 grey--text text--darken-2">División de Objetivos</div>
            <v-row dense align="center">
              <v-col cols="8">
                <v-slider v-model="form.porcentaje_ni" label="% NI" min="0" max="100" thumb-label color="green" track-color="grey lighten-2" :disabled="!puedeEditar" @input="autoBalancearMetas('NI')"/>
              </v-col>
              <v-col cols="4">
                <v-text-field v-model.number="form.porcentaje_ni" type="number" suffix="%" outlined dense hide-details :disabled="!puedeEditar" @input="autoBalancearMetas('NI')"/>
              </v-col>
            </v-row>
            <v-row dense align="center">
              <v-col cols="8">
                <v-slider v-model="form.porcentaje_ri" label="% RI" min="0" max="100" thumb-label color="orange" track-color="grey lighten-2" :disabled="!puedeEditar" @input="autoBalancearMetas('RI')"/>
              </v-col>
              <v-col cols="4">
                <v-text-field v-model.number="form.porcentaje_ri" type="number" suffix="%" outlined dense hide-details :disabled="!puedeEditar" @input="autoBalancearMetas('RI')"/>
              </v-col>
            </v-row>

            <v-btn color="primary" block elevation="1" class="mt-4 text-none font-weight-bold" :loading="loadingSave" :disabled="!puedeEditar" @click="confirmarGuardado">
              <v-icon left>mdi-content-save</v-icon>Guardar Configuración Inicial
            </v-btn>
          </v-form>
        </v-card>
      </v-col>

      <!-- ============ PANEL DERECHO ============ -->
      <v-col cols="12" md="7">
        <!-- Semanas -->
        <v-card outlined class="rounded-lg pa-4 mb-4">
          <div class="d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <v-icon color="secondary" class="mr-2">mdi-calendar-multiselect</v-icon>
              <h3 class="text-h6 font-weight-bold mb-0">Distribución por Semanas</h3>
            </div>
            <v-chip color="primary" outlined small v-if="semanas.length > 0">{{ semanas.length }} semanas</v-chip>
          </div>

          <div v-if="semanas.length === 0" class="text-center py-6">
            <v-icon size="48" color="grey lighten-2">mdi-calendar-question</v-icon>
            <p class="grey--text text--darken-1 mt-3">Selecciona un ciclo ERP y haz clic en <strong>Precargar</strong>, o ingresa las fechas manualmente.</p>
          </div>

          <v-list v-else dense class="transparent">
            <v-list-item v-for="(sem, index) in semanas" :key="index" class="mb-2 rounded-lg elevation-1 bg-white">
              <v-list-item-avatar :color="sem.es_vacaciones ? 'orange lighten-4' : 'primary lighten-4'" class="font-weight-bold" :class="sem.es_vacaciones ? 'orange--text' : 'primary--text'">
                W{{ index + 1 }}
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  Semana {{ index + 1 }}
                  <v-chip x-small color="orange" dark class="ml-1" v-if="sem.es_vacaciones">Vacaciones</v-chip>
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">{{ formatFecha(sem.inicio) }} — {{ formatFecha(sem.fin) }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="d-flex flex-row align-center" style="gap:8px">
                <v-switch v-model="sem.es_vacaciones" dense hide-details color="orange" title="Marcar como semana de vacaciones" class="mt-0 pt-0" :disabled="!puedeEditar" @change="recalcularPesos"/>
                <v-text-field
                  v-model.number="sem.peso_porcentual"
                  label="% MKT" suffix="%" type="number" dense outlined hide-details
                  style="width:90px"
                  class="caption-input"
                  :disabled="!isDirector"
                  @input="validarSumaSemanas"
                />
                <span class="text-body-2 font-weight-bold" :class="{'red--text': !pesoValido, 'green--text': pesoValido}" style="min-width:80px; text-align:right">
                  {{ calcularMontoSemana(sem.peso_porcentual) | currency }}
                </span>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <!-- Alert: valida 100% peso -->
          <v-alert
            v-if="semanas.length > 0"
            :type="semanasValidas ? 'success' : 'error'"
            border="left" dense outlined class="mt-3"
          >
            <v-row align="center" no-gutters>
              <v-col class="font-weight-bold">
                <span>
                  <v-icon small class="mr-1">mdi-calendar-check</v-icon>
                  {{ semanas.length }} semanas configuradas
                </span>
                <span class="mx-2">·</span>
                <span>{{ totalPesoSemanas }}% distribuido</span>
              </v-col>
              <v-col class="text-right text-caption" v-if="!pesoValido">
                El peso debe sumar exactamente 100%
              </v-col>
            </v-row>
          </v-alert>

        </v-card>

        <!-- Feriados / Asuetos -->
        <v-card outlined class="rounded-lg pa-4 mb-4">
          <div class="d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <v-icon color="red darken-1" class="mr-2">mdi-calendar-remove</v-icon>
              <h3 class="text-h6 font-weight-bold mb-0">Feriados / Asuetos</h3>
            </div>
            <v-btn small color="red lighten-4" class="red--text text--darken-1 text-none" elevation="0" :disabled="!puedeEditar" @click="dialogFeriado = true">
              <v-icon left small>mdi-plus</v-icon>Añadir
            </v-btn>
          </div>

          <div v-if="feriados.length === 0" class="text-center py-3">
            <span class="grey--text text-caption">Sin feriados registrados. Al precargar un ciclo se agregan los feriados oficiales del período.</span>
          </div>

          <v-chip-group v-else column>
            <v-chip
              v-for="f in feriados" :key="f.id || f.fecha"
              small close
              :color="f.tipo === 'vacaciones' ? 'orange lighten-4' : 'red lighten-4'"
              :text-color="f.tipo === 'vacaciones' ? 'orange darken-2' : 'red darken-2'"
              :disabled="!puedeEditar"
              @click:close="eliminarFeriado(f)"
            >
              <v-icon left x-small>mdi-calendar-alert</v-icon>
              {{ formatFechaCorta(f.fecha) }} — {{ f.descripcion }}
            </v-chip>
          </v-chip-group>
        </v-card>

        <!-- Quincenas -->
        <v-card outlined class="rounded-lg pa-4 mb-4" v-if="quincenas.length > 0">
          <div class="d-flex align-center mb-3">
            <v-icon color="teal" class="mr-2">mdi-currency-usd</v-icon>
            <h3 class="text-h6 font-weight-bold mb-0">Quincenas del Ciclo</h3>
          </div>
          <v-row dense>
            <v-col v-for="q in quincenas" :key="q.numero" cols="6" sm="4">
              <v-card flat color="teal lighten-5" class="pa-2 rounded-lg text-center">
                <div class="text-caption teal--text text--darken-2 font-weight-bold">Q{{ q.numero }}</div>
                <div class="text-caption text-capitalize">{{ formatQuincenaFecha(q.fin) }}</div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>

        <!-- Presupuesto por Sucursal -->
        <v-card outlined class="rounded-lg pa-4" v-if="presupuestoSucursales.length > 0">
          <div class="d-flex align-center mb-3">
            <v-icon color="indigo" class="mr-2">mdi-office-building-marker</v-icon>
            <h3 class="text-h6 font-weight-bold mb-0">Presupuesto por Sucursal</h3>
          </div>
          <v-simple-table dense class="rounded-lg">
            <template v-slot:default>
              <thead>
                <tr class="grey lighten-4">
                  <th>Plantel</th>
                  <th class="text-right">% Aportación</th>
                  <th class="text-right">Presupuesto</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in presupuestoSucursales" :key="s.id_plantel">
                  <td>{{ s.plantel }}</td>
                  <td class="text-right">
                    <v-chip x-small color="indigo lighten-4" class="indigo--text">{{ s.porcentaje_aportacion }}%</v-chip>
                  </td>
                  <td class="text-right font-weight-bold">{{ s.presupuesto_asignado | currency }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="grey lighten-4 font-weight-bold">
                  <td></td>
                  <td class="text-right">100%</td>
                  <td class="text-right">{{ totalPresupuestoSucursales | currency }}</td>
                </tr>
              </tfoot>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog: Agregar Feriado -->
    <v-dialog v-model="dialogFeriado" max-width="420" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-h6">
          <v-icon left color="red darken-1">mdi-calendar-alert</v-icon>
          Agregar Feriado / Asueto
        </v-card-title>
        <v-card-text>
          <v-menu v-model="menuFeriadoFecha" :close-on-content-click="false" transition="scale-transition" min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="nuevoFeriado.fecha" label="Fecha" prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" outlined dense class="mb-2"/>
            </template>
            <v-date-picker v-model="nuevoFeriado.fecha" @input="menuFeriadoFecha = false" locale="es-mx" color="red darken-1"/>
          </v-menu>
          <v-text-field v-model="nuevoFeriado.descripcion" label="Descripción" outlined dense class="mb-2" placeholder="Ej: Día del Maestro"/>
          <v-select v-model="nuevoFeriado.tipo" :items="tiposFeriado" label="Tipo" outlined dense/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="dialogFeriado = false">Cancelar</v-btn>
          <v-btn color="red darken-1" dark :disabled="!nuevoFeriado.fecha || !nuevoFeriado.descripcion" @click="agregarFeriado">
            <v-icon left small>mdi-check</v-icon>Agregar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Dialog: Confirmar Guardado -->
    <v-dialog v-model="dialogConfirmarGuardado" max-width="400" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-h6 pb-2">
          <v-icon left color="warning">mdi-content-save-alert</v-icon>
          Confirmar Guardado
        </v-card-title>
        <v-card-text class="pt-2 text-body-1">
          ¿Estás seguro de que deseas guardar esta configuración? <br><br>
          <strong class="red--text text--darken-2" v-if="!cicloCreacion && idPresupuestoCiclo">Estás editando un ciclo existente.</strong>
          <strong class="green--text text--darken-2" v-else>Estás creando una nueva apertura presupuestal.</strong>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer/>
          <v-btn text @click="dialogConfirmarGuardado = false" :disabled="loadingSave">Cancelar</v-btn>
          <v-btn color="primary" elevation="1" :loading="loadingSave" @click="procesarGuardado">
            <v-icon left small>mdi-check</v-icon> Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment';
const XLSX = require('xlsx');

const normalizar = (s) => String(s == null ? '' : s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9%]/g, '');
const numeroExcel = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return v;
  const n = parseFloat(String(v).replace(/[$,%\s]/g, ''));
  return isNaN(n) ? null : n;
};
const fechaExcel = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : moment(v).format('YYYY-MM-DD');
  if (typeof v === 'number') {
    const d = XLSX.SSF.parse_date_code(Math.round(v));
    return d ? moment({ year: d.y, month: d.m - 1, day: d.d }).format('YYYY-MM-DD') : null;
  }
  const m = moment(String(v).trim(), ['YYYY-MM-DD', 'DD/MM/YYYY', 'D/M/YYYY'], true);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
};
const filasHoja = (libro, nombre) => {
  const hoja = libro.Sheets[Object.keys(libro.Sheets).find(n => normalizar(n) === normalizar(nombre))];
  if (!hoja) return null;
  return XLSX.utils.sheet_to_json(hoja, { defval: '' }).map(fila => {
    const out = {};
    Object.keys(fila).forEach(k => { out[normalizar(k)] = fila[k]; });
    return out;
  });
};

export default {
  name: 'TabConfiguracion',
  props: {
    instancia: { type: String, required: true },
    isDirector: { type: Boolean, default: false },
    cicloId: { type: [Number, String], default: null },
    isActivo: { type: Boolean, default: false },
    cicloCreacion: { type: Boolean, default: false },
    hasClonar: { type: Boolean, default: false },
  },

  data() {
    return {
      valid: true,
      menuStart: false,
      menuEnd: false,
      loadingSave: false,
      loadingCiclos: false,
      loadingPrecargar: false,
      loadingClonar: false,
      loadingImportar: false,
      importErrores: [],
      importAvisos: [],
      dialogConfirmarGuardado: false,

      // Ciclos ERP
      ciclosErp: [],
      cicloErpSeleccionado: null,

      // Formulario principal
      form: {
        fecha_inicio: '',
        fecha_fin: '',
        ingreso_base: 0,
        venta_total: 0,
        porcentaje_autorizado: 30,
        bolsa_total: 0,
        porcentaje_ni: 70,
        porcentaje_ri: 30,
        estado: 'Borrador'
      },
      estadosCiclo: ['Borrador', 'Autorizado', 'Cerrado'],

      // Semanas
      semanas: [],
      pesoValido: false,

      // Feriados
      feriados: [],
      dialogFeriado: false,
      menuFeriadoFecha: false,
      nuevoFeriado: { fecha: '', descripcion: '', tipo: 'feriado' },
      tiposFeriado: [
        { text: 'Feriado oficial', value: 'feriado' },
        { text: 'Semana de vacaciones', value: 'vacaciones' },
        { text: 'Evento especial', value: 'evento' }
      ],

      // Quincenas
      quincenas: [],

      // Sucursales
      presupuestoSucursales: [],

      // ID del ciclo en nuestra BD (después de guardar)
      idPresupuestoCiclo: null
    };
  },

  computed: {
    puedeEditar() {
      return this.isDirector && (this.isActivo || this.cicloCreacion);
    },
    totalPesoSemanas() {
      const s = this.semanas.reduce((acc, s) => acc + (parseFloat(s.peso_porcentual) || 0), 0);
      return Math.round(s * 100) / 100;
    },
    semanasOperativas() {
      return this.semanas.filter(s => !s.es_vacaciones);
    },
    semanasValidas() {
      // Requiere que el peso sume 100% sin importar la cantidad de semanas operativas
      return this.semanas.length > 0 && this.totalPesoSemanas === 100;
    },
    totalPresupuestoSucursales() {
      return this.presupuestoSucursales.reduce((a, s) => a + (parseFloat(s.presupuesto_asignado) || 0), 0);
    },
    datosUsuario() {
      return this.$store.getters['login/getdatosUsuarioERPERP'] || { iderp: 1 };
    }
  },

  watch: {
    cicloId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.cargarCicloPorId(newVal);
        } else if (this.cicloCreacion) {
          this.limpiarFormulario();
        }
      }
    }
  },

  mounted() {
    this.cargarCiclosERP();
  },

  methods: {
    // ---------- Carga Local e Historial ----------
    limpiarFormulario() {
      this.idPresupuestoCiclo = null;
      this.cicloErpSeleccionado = null;
      this.form = { fecha_inicio: '', fecha_fin: '', ingreso_base: 0, venta_total: 0, porcentaje_autorizado: 30, bolsa_total: 0, porcentaje_ni: 70, porcentaje_ri: 30, estado: 'Borrador' };
      this.semanas = [];
      this.quincenas = [];
      this.feriados = [];
      this.presupuestoSucursales = [];
      this.pesoValido = false;
      if (this.$refs.formConfig) this.$refs.formConfig.resetValidation();
    },

    async cargarCicloPorId(id) {
      if (!id) return;
      this.loadingPrecargar = true;
      try {
        const [resCiclo, resSemanas, resFeriados, resSucursales] = await Promise.all([
          this.$http.get(`api/presupuestos/ciclo/${id}`),
          this.$http.get(`api/presupuestos/semanas/${id}`),
          this.$http.get(`api/presupuestos/feriados/${id}`),
          this.$http.get(`api/presupuestos/sucursales/${id}`)
        ]);

        const c = resCiclo.data;
        if (c) {
          this.idPresupuestoCiclo = c.id;
          this.form.fecha_inicio = moment(c.fecha_inicio).format('YYYY-MM-DD');
          this.form.fecha_fin = moment(c.fecha_fin).format('YYYY-MM-DD');
          this.form.ingreso_base = c.ingreso_base;
          this.form.venta_total = c.venta_total;
          this.form.porcentaje_autorizado = c.porcentaje_autorizado;
          this.form.bolsa_total = c.bolsa_total;
          this.form.porcentaje_ni = c.porcentaje_ni;
          this.form.porcentaje_ri = c.porcentaje_ri;
          this.form.estado = c.estado || 'Borrador';
          
          this.calcularQuincenas(this.form.fecha_inicio, this.form.fecha_fin);
        }

        this.semanas = (resSemanas.data || []).map(s => ({
          id: s.id,
          numero: s.numero_semana,
          inicio: moment(s.fecha_inicio_semana).format('YYYY-MM-DD'),
          fin: moment(s.fecha_fin_semana).format('YYYY-MM-DD'),
          es_vacaciones: Boolean(s.es_vacaciones),
          peso_porcentual: parseFloat(s.peso_porcentual)
        }));
        this.validarSumaSemanas();

        this.feriados = resFeriados.data || [];
        this.presupuestoSucursales = resSucursales.data || [];

      } catch (e) {
        console.error("Error cargando el ciclo seleccionado:", e);
        this.$root.$emit('mostrarAlerta', { tipo: 'error', mensaje: 'Error al cargar los datos del ciclo' });
      } finally {
        this.loadingPrecargar = false;
      }
    },

    // ---------- ERP ----------
    async cargarCiclosERP() {
      this.loadingCiclos = true;
      try {
        const res = await this.$http.get('api/presupuestos/ciclos-erp');
        // Filtrar solo ciclos relevantes de la instancia (INBI o FAST)
        const keyword = this.instancia === 'FAST' ? 'FE' : '';
        this.ciclosErp = keyword
          ? res.data.filter(c => c.ciclo && c.ciclo.includes(keyword))
          : res.data.filter(c => c.ciclo && !c.ciclo.includes('FE'));
      } catch (e) {
        console.error('Error cargando ciclos ERP:', e);
      } finally {
        this.loadingCiclos = false;
      }
    },

    async precargarDesdeCicloERP() {
      if (!this.cicloErpSeleccionado) return;
      this.loadingPrecargar = true;
      try {
        const res = await this.$http.get(
          `api/presupuestos/precargar/${this.cicloErpSeleccionado.id_ciclo}?instancia=${this.instancia}`
        );
        const d = res.data;

        // Rellenar formulario
        this.form.fecha_inicio     = d.fecha_inicio || '';
        this.form.fecha_fin        = d.fecha_fin    || '';
        this.form.ingreso_base     = d.ingreso_base || 0;
        this.form.venta_total      = d.venta_total  || 0; 
        this.calcularBolsa();

        // Semanas
        this.semanas = (d.semanas || []).map(s => ({ ...s }));
        this.validarSumaSemanas();

        // Quincenas
        this.quincenas = d.quincenas || [];

        // Feriados precargados (solo si no hay ya)
        if (this.feriados.length === 0 && d.feriados_precargados && d.feriados_precargados.length > 0) {
          this.feriados = d.feriados_precargados.map(f => ({ ...f, id: null }));
        }

        // Sucursales
        this.presupuestoSucursales = d.presupuesto_sucursales || [];

        this.$root.$emit('mostrarAlerta', { tipo: 'success', mensaje: `Ciclo "${d.ciclo}" precargado correctamente` });
      } catch (e) {
        console.error('Error precargando ciclo:', e);
        this.$root.$emit('mostrarAlerta', { tipo: 'error', mensaje: 'Error al precargar el ciclo desde el ERP' });
      } finally {
        this.loadingPrecargar = false;
      }
    },

    // ---------- Cálculos ----------
    calcularBolsa() {
      if (this.form.ingreso_base && this.form.porcentaje_autorizado) {
        this.form.bolsa_total = this.form.ingreso_base * (this.form.porcentaje_autorizado / 100);
      } else {
        this.form.bolsa_total = 0;
      }
    },

    autoBalancearMetas(origen) {
      if (origen === 'NI') this.form.porcentaje_ri = 100 - this.form.porcentaje_ni;
      else this.form.porcentaje_ni = 100 - this.form.porcentaje_ri;
    },

    calcularSemanas() {
      if (!this.form.fecha_inicio || !this.form.fecha_fin) return;
      const inicio = moment(this.form.fecha_inicio);
      const fin    = moment(this.form.fecha_fin);
      if (fin.isBefore(inicio)) { this.semanas = []; return; }

      this.semanas = [];
      let current  = inicio.clone();
      let num      = 0;

      // Sin límite total: genera todas las semanas del rango.
      // El ciclo siempre debe tener 4 operativas; si hay vacaciones el total puede ser 5+.
      while (current.isSameOrBefore(fin)) {
        let weekStart = current.clone();
        let weekEnd   = weekStart.clone().endOf('isoWeek');
        
        if (num === 0) {
           // Si es la primera semana, debe tener 9 días
           // Es decir, agregaremos una semana adicional
           weekEnd.add(1, 'w');
        }

        if (weekEnd.isAfter(fin)) weekEnd = fin.clone();

        num++;
        this.semanas.push({
          numero:          num,
          inicio:          weekStart.format('YYYY-MM-DD'),
          fin:             weekEnd.format('YYYY-MM-DD'),
          es_vacaciones:   false,
          peso_porcentual: 0
        });
        current = weekEnd.clone().add(1, 'days');
      }

      this.recalcularPesos();
      this.calcularQuincenas(this.form.fecha_inicio, this.form.fecha_fin);
    },

    calcularQuincenas(fechaIni, fechaFin) {
      if (!fechaIni || !fechaFin) { this.quincenas = []; return; }
      const ini  = moment(fechaIni);
      const fin  = moment(fechaFin);
      this.quincenas = [];
      let cursor = ini.clone();
      let num    = 1;
      while (cursor.isSameOrBefore(fin)) {
        let qIni, qFin;
        if (cursor.date() <= 15) {
          qIni = cursor.clone().startOf('month');
          qFin = cursor.clone().date(15);
        } else {
          qIni = cursor.clone().date(16);
          qFin = cursor.clone().endOf('month');
        }
        if (qIni.isBefore(ini)) qIni = ini.clone();
        // Removemos la limitante de ciclo para fin de quincena natural
        // if (qFin.isAfter(fin))  qFin = fin.clone();
        this.quincenas.push({ numero: num++, inicio: qIni.format('YYYY-MM-DD'), fin: qFin.format('YYYY-MM-DD') });
        cursor = qFin.clone().add(1, 'days');
      }
    },

    recalcularPesos() {
      const semanasOp = this.semanas.filter(s => !s.es_vacaciones);
      const n = semanasOp.length;
      if (n === 0) return;
      const pesoBase = Math.floor((100 / n) * 100) / 100;
      let acum = 0;
      this.semanas.forEach(s => {
        if (!s.es_vacaciones) {
          const esUlt = semanasOp.indexOf(s) === semanasOp.length - 1;
          s.peso_porcentual = esUlt ? Math.round((100 - acum) * 100) / 100 : pesoBase;
          acum += s.peso_porcentual;
        } else {
          s.peso_porcentual = 0;
        }
      });
      this.validarSumaSemanas();
    },

    validarSumaSemanas() {
      // pesoValido: sigue usando solo los % para compatibilidad con el alert de peso
      this.pesoValido = this.totalPesoSemanas === 100;
    },

    calcularMontoSemana(p) {
      if (!p) return 0;
      return this.form.bolsa_total * (p / 100);
    },

    // ---------- Feriados ----------
    agregarFeriado() {
      if (!this.nuevoFeriado.fecha || !this.nuevoFeriado.descripcion) return;
      this.feriados.push({ ...this.nuevoFeriado, id: null });
      this.nuevoFeriado = { fecha: '', descripcion: '', tipo: 'feriado' };
      this.dialogFeriado = false;
    },

    eliminarFeriado(f) {
      const idx = this.feriados.indexOf(f);
      if (idx > -1) this.feriados.splice(idx, 1);

      // Si ya tiene ID en BD, borrarlo
      if (f.id && this.idPresupuestoCiclo) {
        this.$http.delete(`api/presupuestos/feriados/${f.id}`).catch(e => console.error(e));
      }
    },

    // ---------- Clonar ----------
    async clonarCicloAnterior() {
      this.loadingClonar = true;
      try {
        const res = await this.$http.get(`api/presupuestos/ciclos/${this.instancia}`);
        const ciclos = res.data || [];
        if(ciclos.length === 0) {
           this.$root.$emit('mostrarAlerta', { tipo: 'warning', mensaje: 'No hay ciclos históricos para clonar' });
           return;
        }
        const ultimo = ciclos[0]; // Orden DESC por id
        
        // Consultamos los detalles del ciclo
        await this.cargarCicloPorId(ultimo.id);

        // Ahora LIMPIAMOS los IDs para que sea reconocido como NUEVO al guardar
        this.idPresupuestoCiclo = null; 
        this.semanas.forEach(s => s.id = null);
        this.feriados.forEach(f => f.id = null);
        // Las sucursales se re-insertarán basándose en el nuevo idPresupuestoCiclo
        
        this.$root.$emit('mostrarAlerta', { tipo: 'success', mensaje: 'Configuración clonada correctamente, recuerda guardar.' });

      } catch(err) {
        console.error("Error clonando el ciclo:", err);
        this.$root.$emit('mostrarAlerta', { tipo: 'error', mensaje: 'Error al clonar configuración' });
      } finally {
        this.loadingClonar = false;
      }
    },

    // ---------- Excel ----------
    async sucursalesParaPlantilla() {
      if (this.presupuestoSucursales.length) return this.presupuestoSucursales;
      try {
        const res = await this.$http.get(`api/presupuestos/ciclos/${this.instancia}`);
        const ultimo = (res.data || [])[0];
        if (!ultimo) return [];
        const suc = await this.$http.get(`api/presupuestos/sucursales/${ultimo.id}`);
        return suc.data || [];
      } catch (e) {
        return [];
      }
    },

    async descargarPlantilla() {
      const sucursales = await this.sucursalesParaPlantilla();
      const libro = XLSX.utils.book_new();
      const hoja = (filas, anchos) => {
        const ws = XLSX.utils.aoa_to_sheet(filas);
        ws['!cols'] = anchos.map(wch => ({ wch }));
        return ws;
      };

      XLSX.utils.book_append_sheet(libro, hoja([
        ['Campo', 'Valor', 'Nota'],
        ['Instancia', this.instancia, 'INBI o FAST. Debe coincidir con la instancia del panel'],
        ['Fecha inicio', this.form.fecha_inicio || '', 'AAAA-MM-DD'],
        ['Fecha fin', this.form.fecha_fin || '', 'AAAA-MM-DD'],
        ['Venta total ciclo anterior', this.form.venta_total || 0, 'Opcional, solo informativo'],
        ['Ingreso base', this.form.ingreso_base || 0, 'Base de la bolsa: bolsa = ingreso base × % autorizado'],
        ['% Autorizado MKT', this.form.porcentaje_autorizado || 30, 'De 1 a 100'],
        ['% NI', this.form.porcentaje_ni, '% NI + % RI = 100'],
        ['% RI', this.form.porcentaje_ri, ''],
        ['Estado', this.form.estado || 'Borrador', 'Borrador, Autorizado o Cerrado'],
      ], [28, 18, 60]), 'Ciclo');

      XLSX.utils.book_append_sheet(libro, hoja([
        ['id_plantel', 'Plantel', 'Venta ciclo anterior', '% Aportación', 'Presupuesto asignado'],
        ...sucursales.map(s => [s.id_plantel, s.plantel, Number(s.venta_ciclo_anterior) || 0, Number(s.porcentaje_aportacion) || 0, Number(s.presupuesto_asignado) || 0]),
      ], [12, 28, 20, 14, 22]), 'Sucursales');

      XLSX.utils.book_append_sheet(libro, hoja([
        ['Semana', 'Inicio', 'Fin', 'Vacaciones (SI/NO)', '% Peso MKT'],
        ...this.semanas.map((s, i) => [i + 1, s.inicio, s.fin, s.es_vacaciones ? 'SI' : 'NO', Number(s.peso_porcentual) || 0]),
      ], [10, 14, 14, 20, 14]), 'Semanas');

      XLSX.utils.book_append_sheet(libro, hoja([
        ['Fecha', 'Descripción', 'Tipo'],
        ...this.feriados.map(f => [moment(f.fecha).format('YYYY-MM-DD'), f.descripcion, f.tipo || 'feriado']),
      ], [14, 40, 14]), 'Feriados');

      XLSX.utils.book_append_sheet(libro, hoja([
        ['Cómo llenar la plantilla'],
        ['1. Ciclo: llena la columna Valor. Las fechas van como AAAA-MM-DD (ej. 2026-11-09).'],
        ['2. Sucursales: una fila por plantel. No cambies el id_plantel. Llena % Aportación o Presupuesto asignado: si falta uno, se calcula con el ingreso base.'],
        ['3. Semanas: opcional. Si la dejas vacía, las semanas se calculan con las fechas del ciclo. Si la llenas, el % Peso MKT debe sumar 100 y las semanas de vacaciones van en 0.'],
        ['4. Feriados: opcional. Tipo: feriado, vacaciones o evento.'],
        ['5. En el panel: pestaña Configuración Inicial > Importar Excel. El archivo solo llena el formulario; revisa y presiona "Guardar Configuración Inicial".'],
        ['6. Si en pantalla hay un ciclo abierto, al guardar se actualiza ese ciclo. Para crear uno nuevo, primero presiona "Nuevo".'],
        ['Los datos que trae esta plantilla son del ciclo que estaba en pantalla al descargarla; sirven de ejemplo.'],
      ], [140]), 'Instrucciones');

      XLSX.writeFile(libro, `plantilla-presupuesto-${this.instancia}.xlsx`);
    },

    importarExcel(evento) {
      const archivo = evento.target.files && evento.target.files[0];
      evento.target.value = '';
      if (!archivo) return;
      this.importErrores = [];
      this.importAvisos = [];
      this.loadingImportar = true;
      const lector = new FileReader();
      lector.onload = (e) => {
        try {
          const libro = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
          this.aplicarExcel(libro);
        } catch (err) {
          console.error('Error leyendo Excel:', err);
          this.importErrores = ['No se pudo leer el archivo. Usa la plantilla en formato .xlsx.'];
        } finally {
          this.loadingImportar = false;
        }
      };
      lector.onerror = () => {
        this.importErrores = ['No se pudo leer el archivo.'];
        this.loadingImportar = false;
      };
      lector.readAsArrayBuffer(archivo);
    },

    aplicarExcel(libro) {
      const errores = [];
      const avisos = [];

      const filasCiclo = filasHoja(libro, 'Ciclo');
      if (!filasCiclo) {
        this.importErrores = ['Falta la hoja "Ciclo". Descarga la plantilla y llénala.'];
        return;
      }
      const ciclo = {};
      filasCiclo.forEach(f => { ciclo[normalizar(f.campo)] = f.valor; });

      const instancia = String(ciclo.instancia || '').trim().toUpperCase();
      if (instancia && instancia !== this.instancia) errores.push(`La plantilla es de ${instancia} y el panel está en ${this.instancia}.`);

      const fechaInicio = fechaExcel(ciclo.fechainicio);
      const fechaFin = fechaExcel(ciclo.fechafin);
      if (!fechaInicio) errores.push('Ciclo: "Fecha inicio" vacía o con formato inválido (usa AAAA-MM-DD).');
      if (!fechaFin) errores.push('Ciclo: "Fecha fin" vacía o con formato inválido (usa AAAA-MM-DD).');
      if (fechaInicio && fechaFin && fechaFin < fechaInicio) errores.push('Ciclo: la fecha fin es anterior a la fecha inicio.');

      const ingresoBase = numeroExcel(ciclo.ingresobase);
      if (!ingresoBase || ingresoBase <= 0) errores.push('Ciclo: "Ingreso base" debe ser mayor a cero.');
      const pctAutorizado = numeroExcel(ciclo['%autorizadomkt']);
      if (pctAutorizado === null || pctAutorizado < 1 || pctAutorizado > 100) errores.push('Ciclo: "% Autorizado MKT" debe estar entre 1 y 100.');

      let pctNi = numeroExcel(ciclo['%ni']);
      let pctRi = numeroExcel(ciclo['%ri']);
      if (pctNi === null && pctRi !== null) pctNi = 100 - pctRi;
      if (pctRi === null && pctNi !== null) pctRi = 100 - pctNi;
      if (pctNi === null || pctRi === null || Math.round((pctNi + pctRi) * 100) !== 10000) errores.push('Ciclo: % NI + % RI debe sumar 100.');

      let estado = String(ciclo.estado || '').trim();
      const estadoValido = this.estadosCiclo.find(x => normalizar(x) === normalizar(estado));
      if (!estadoValido) {
        if (estado) avisos.push(`Estado "${estado}" no válido; se usará Borrador.`);
        estado = 'Borrador';
      } else {
        estado = estadoValido;
      }

      const sucursales = [];
      const vistos = new Set();
      (filasHoja(libro, 'Sucursales') || []).forEach((f, i) => {
        const fila = i + 2;
        if (f.idplantel === '' && f.plantel === '') return;
        const id = numeroExcel(f.idplantel);
        if (!id) { errores.push(`Sucursales, fila ${fila}: falta id_plantel.`); return; }
        if (vistos.has(id)) { errores.push(`Sucursales, fila ${fila}: el id_plantel ${id} está repetido.`); return; }
        vistos.add(id);
        let pct = numeroExcel(f['%aportacion']);
        let monto = numeroExcel(f.presupuestoasignado);
        if (pct === null && monto === null) { errores.push(`Sucursales, fila ${fila}: llena % Aportación o Presupuesto asignado.`); return; }
        if (monto === null && ingresoBase) monto = Math.round(ingresoBase * (pct / 100) * 100) / 100;
        if (pct === null && ingresoBase) pct = Math.round((monto / ingresoBase) * 10000) / 100;
        sucursales.push({
          id_plantel: id,
          plantel: String(f.plantel || '').trim() || `Plantel ${id}`,
          venta_ciclo_anterior: numeroExcel(f.ventacicloanterior) || 0,
          porcentaje_aportacion: pct || 0,
          presupuesto_asignado: monto || 0
        });
      });
      if (!sucursales.length) {
        avisos.push('La hoja "Sucursales" está vacía: se conservará el presupuesto por sucursal que está en pantalla.');
      } else {
        const sumaPct = Math.round(sucursales.reduce((a, s) => a + s.porcentaje_aportacion, 0) * 100) / 100;
        if (Math.abs(sumaPct - 100) > 0.5) avisos.push(`Sucursales: el % de aportación suma ${sumaPct}%, no 100%.`);
      }

      const semanas = [];
      (filasHoja(libro, 'Semanas') || []).forEach((f, i) => {
        const fila = i + 2;
        if (f.inicio === '' && f.fin === '') return;
        const inicio = fechaExcel(f.inicio);
        const fin = fechaExcel(f.fin);
        if (!inicio || !fin) { errores.push(`Semanas, fila ${fila}: fechas inválidas (usa AAAA-MM-DD).`); return; }
        const vacaciones = ['si', 'sí', 's', '1', 'true', 'x'].includes(String(f.vacacionessino).trim().toLowerCase());
        semanas.push({
          numero: semanas.length + 1,
          inicio,
          fin,
          es_vacaciones: vacaciones,
          peso_porcentual: vacaciones ? 0 : (numeroExcel(f['%pesomkt']) || 0)
        });
      });

      const feriados = [];
      (filasHoja(libro, 'Feriados') || []).forEach((f, i) => {
        if (f.fecha === '' && f.descripcion === '') return;
        const fecha = fechaExcel(f.fecha);
        if (!fecha || !String(f.descripcion).trim()) { errores.push(`Feriados, fila ${i + 2}: falta fecha válida o descripción.`); return; }
        const tipo = ['feriado', 'vacaciones', 'evento'].includes(normalizar(f.tipo)) ? normalizar(f.tipo) : 'feriado';
        feriados.push({ id: null, fecha, descripcion: String(f.descripcion).trim(), tipo });
      });

      if (errores.length) {
        this.importErrores = errores;
        return;
      }

      this.form.fecha_inicio = fechaInicio;
      this.form.fecha_fin = fechaFin;
      this.form.venta_total = numeroExcel(ciclo.ventatotalcicloanterior) || 0;
      this.form.ingreso_base = ingresoBase;
      this.form.porcentaje_autorizado = pctAutorizado;
      this.form.porcentaje_ni = pctNi;
      this.form.porcentaje_ri = pctRi;
      this.form.estado = estado;
      this.calcularBolsa();

      if (semanas.length) {
        this.semanas = semanas;
        this.validarSumaSemanas();
        this.calcularQuincenas(fechaInicio, fechaFin);
        if (!this.pesoValido) avisos.push(`Semanas: el % Peso MKT suma ${this.totalPesoSemanas}%; debe sumar 100 para poder guardar.`);
      } else {
        this.calcularSemanas();
        avisos.push('Semanas calculadas con las fechas del ciclo y peso repartido en partes iguales.');
      }

      const fechasExistentes = new Set(this.feriados.map(f => moment(f.fecha).format('YYYY-MM-DD')));
      const nuevosFeriados = feriados.filter(f => !fechasExistentes.has(f.fecha));
      this.feriados = this.feriados.concat(nuevosFeriados);
      if (feriados.length && nuevosFeriados.length < feriados.length) avisos.push('Algunos feriados ya estaban registrados y no se duplicaron.');

      if (sucursales.length) this.presupuestoSucursales = sucursales;

      avisos.push(!this.cicloCreacion && this.idPresupuestoCiclo
        ? 'Al guardar se ACTUALIZARÁ el ciclo que está en pantalla. Para crear uno nuevo, presiona "Nuevo" e importa otra vez.'
        : 'Al guardar se creará un ciclo nuevo.');
      avisos.push('Nada se ha guardado todavía: presiona "Guardar Configuración Inicial".');
      this.importAvisos = avisos;
    },

    // ---------- Guardar ----------
    confirmarGuardado() {
      if (!this.$refs.formConfig.validate()) return;
      if (!this.semanasValidas) {
        const msg = this.semanasOperativas.length !== 4
          ? `El ciclo debe tener exactamente 4 semanas operativas (actualmente: ${this.semanasOperativas.length}). Marca las semanas de vacaciones con el toggle.`
          : 'La distribución de peso debe sumar exactamente 100%';
        this.$root.$emit('mostrarAlerta', { tipo: 'error', mensaje: msg });
        return;
      }
      this.dialogConfirmarGuardado = true;
    },

    async procesarGuardado() {
      this.dialogConfirmarGuardado = false;
      this.loadingSave = true;
      try {
        // 1. Ciclo base
        const payloadCiclo = {
          id_ciclo:             parseInt(moment(this.form.fecha_inicio).format('YYYYMM')),
          instancia:            this.instancia,
          ingreso_base:         this.form.ingreso_base,
          venta_total:          this.form.venta_total,
          porcentaje_autorizado:this.form.porcentaje_autorizado,
          bolsa_total:          this.form.bolsa_total,
          porcentaje_ni:        this.form.porcentaje_ni,
          porcentaje_ri:        this.form.porcentaje_ri,
          fecha_inicio:         this.form.fecha_inicio,
          fecha_fin:            this.form.fecha_fin,
          estado:               this.form.estado || 'Borrador',
          usuario_registro:     this.datosUsuario.iderp || 1
        };

        let idInsertado;
        if (!this.cicloCreacion && this.idPresupuestoCiclo) {
          // Edición (PUT)
          await this.$http.put('api/presupuestos/ciclos/' + this.idPresupuestoCiclo, payloadCiclo);
          idInsertado = this.idPresupuestoCiclo;
        } else {
          // Creación (POST)
          const resCiclo = await this.$http.post('api/presupuestos/ciclos', payloadCiclo);
          idInsertado = resCiclo.data.id || resCiclo.data.insertId;
          this.idPresupuestoCiclo = idInsertado;
        }

        // 2. Semanas
        if (idInsertado && this.semanas.length > 0) {
          await this.$http.post('api/presupuestos/semanas', {
            id_presupuesto_ciclo: idInsertado,
            semanas: this.semanas.map(s => ({
              numero_semana:         s.numero,
              fecha_inicio_semana:   s.inicio,
              fecha_fin_semana:      s.fin,
              peso_porcentual:       s.peso_porcentual || 0,
              porcentaje_asignado_ni:this.form.porcentaje_ni,
              porcentaje_asignado_ri:this.form.porcentaje_ri,
              canales:               []
            }))
          });
        }

        // 3. Feriados
        if (idInsertado && this.feriados.length > 0) {
          const feriadosSinId = this.feriados.filter(f => !f.id);
          for (const f of feriadosSinId) {
            const saved = await this.$http.post('api/presupuestos/feriados', {
              id_presupuesto_ciclo: idInsertado,
              fecha:       f.fecha,
              descripcion: f.descripcion,
              tipo:        f.tipo || 'feriado'
            });
            f.id = saved.data.id;
          }
        }

        // 4. Presupuesto por sucursal
        if (idInsertado && this.presupuestoSucursales.length > 0) {
          await this.$http.post('api/presupuestos/sucursales', {
            id_presupuesto_ciclo: idInsertado,
            sucursales: this.presupuestoSucursales
          });
        }

        this.$emit('nuevo-ciclo-guardado');
        this.$root.$emit('mostrarAlerta', { tipo: 'success', mensaje: 'Ciclo guardado correctamente' });
      } catch (error) {
        console.error(error);
        this.$root.$emit('mostrarAlerta', { tipo: 'error', mensaje: 'Error al guardar la configuración' });
      } finally {
        this.loadingSave = false;
      }
    },

    // ---------- Formato ----------
    formatFecha(f) { return moment(f).format('DD MMM'); },
    formatFechaCorta(f) { return moment(f).format('DD/MM'); },
    formatQuincenaFecha(f) {
      moment.locale('es');
      return moment(f).format('dddd D MMM, YYYY');
    }
  },

  filters: {
    currency(value) {
      if (typeof value !== 'number') return value;
      return '$' + value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    }
  }
};
</script>

<style scoped>
.bg-light-grey { background-color: #f9f9f9; }
.caption-input :deep(input) { font-size: 0.82rem !important; }
.transparent { background-color: transparent !important; }
</style>
